<template>
  <section class="page" data-testid="page-stickers">
    <header class="page-header">
      <div><h1>表情包</h1><p>元数据来自 SQLite，图片仍保留在文件系统。</p></div>
      <div class="actions">
        <button class="button" :disabled="!canCopyDiagnostics" @click="copyDiagnostics">复制诊断</button>
        <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>
    <div class="metrics-grid">
      <MetricCard label="总数" :value="overview.total || stickers.length" />
      <MetricCard label="已分析" :value="analyzedCount" />
      <MetricCard label="待分析" :value="pendingCount" />
      <MetricCard label="当前匹配" :value="filteredStickers.length" />
      <MetricCard label="可发送" :value="sendableCount" />
      <MetricCard label="召回候选" :value="candidates.length" />
      <MetricCard label="最近决策" :value="decisions.length" />
    </div>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <p v-if="error" class="error-text">{{ error }}</p>
    <div class="panel">
      <div class="toolbar">
        <input type="file" :disabled="loading" @change="upload" />
        <input v-model="filterText" placeholder="搜索文件、描述、关键词" />
        <button class="button" :class="{ primary: statusFilter === 'all' }" @click="statusFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: statusFilter === 'analyzed' }" @click="statusFilter = 'analyzed'">已分析</button>
        <button class="button" :class="{ primary: statusFilter === 'pending' }" @click="statusFilter = 'pending'">待分析</button>
        <button class="button" :class="{ primary: statusFilter === 'used' }" @click="statusFilter = 'used'">用过</button>
        <button class="button" :disabled="loading" @click="analyzePending">分析待处理</button>
        <button class="button" :disabled="loading" @click="reindex">重建索引</button>
      </div>
      <DataTable :columns="columns" :rows="filteredStickers" row-key="hash">
        <template #preview="{ row }"><img class="thumb" :src="`/api/stickers/image/${row.hash}`" /></template>
        <template #analyzed="{ row }"><StatusBadge :status="row.analyzed ? 'ok' : 'pending'" /></template>
        <template #keywords="{ row }"><span>{{ (row.keywords || []).join(", ") || "-" }}</span></template>
        <template #send_count="{ row }">{{ row.send_count || 0 }} / {{ row.recv_count || 0 }}</template>
        <template #actions="{ row }">
          <div class="row-actions">
            <button class="button small" :disabled="loading" @click="selectSticker(row)">详情</button>
            <button class="button small" :disabled="loading" @click="analyze(row.hash)">分析</button>
            <button class="button small" @click="copySticker(row)">复制</button>
            <button class="button small danger" :disabled="loading" @click="remove(row.hash)">删除</button>
          </div>
        </template>
      </DataTable>
    </div>
    <div class="split">
      <div class="panel">
        <h2>召回调试</h2>
        <div class="runtime-grid compact" v-if="candidates.length">
          <div><span>候选数</span><strong>{{ candidates.length }}</strong></div>
          <div><span>最高分</span><strong>{{ formatScore(topCandidateScore) }}</strong></div>
          <div><span>命中来源</span><strong>{{ candidateSourceSummary }}</strong></div>
        </div>
        <div class="toolbar">
          <input v-model="searchText" placeholder="输入消息或想要的表情" @keyup.enter="search" />
          <select v-model.number="searchTopK">
            <option :value="5">Top 5</option>
            <option :value="8">Top 8</option>
            <option :value="12">Top 12</option>
          </select>
          <button class="button primary" :disabled="loading || !searchText.trim()" @click="search">搜索</button>
        </div>
        <DataTable :columns="candidateColumns" :rows="candidates" row-key="hash">
          <template #preview="{ row }"><img class="thumb" :src="`/api/stickers/image/${row.hash}`" /></template>
          <template #score="{ row }">{{ formatScore(row.final_score ?? row.score) }}</template>
          <template #match_reasons="{ row }">{{ (row.match_reasons || row.candidate_sources || []).join(", ") || "-" }}</template>
          <template #action="{ row }">
            <div class="row-actions">
              <button class="button small" @click="selectedCandidate = row">查看</button>
              <button class="button small" @click="copyCandidate(row)">复制</button>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="panel">
        <h2>最近决策</h2>
        <div class="toolbar">
          <button class="button" :class="{ primary: decisionFilter === 'all' }" @click="decisionFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: decisionFilter === 'sent' }" @click="decisionFilter = 'sent'">已发送</button>
          <button class="button" :class="{ primary: decisionFilter === 'skipped' }" @click="decisionFilter = 'skipped'">未发送</button>
        </div>
        <DataTable :columns="decisionColumns" :rows="filteredDecisionRows" row-key="key">
          <template #selected_hash="{ row }">
            <span>{{ row.selected_hash || row.decision?.selected_hash || "-" }}</span>
          </template>
          <template #status="{ row }">
            <span class="status-badge" :class="row.action === 'send' || row.result === 'sent' ? 'ok' : 'pending'">
              {{ row.action || row.result || row.failure_reason || "none" }}
            </span>
          </template>
          <template #detail="{ row }"><pre>{{ decisionDetail(row) }}</pre></template>
        </DataTable>
      </div>
    </div>
    <div class="split">
      <div class="panel">
        <h2>表情详情</h2>
        <div v-if="selectedSticker" class="sticker-detail">
          <img class="detail-thumb" :src="`/api/stickers/image/${selectedSticker.hash}`" />
          <div class="runtime-grid compact">
            <div><span>文件</span><strong>{{ selectedSticker.filename || "-" }}</strong></div>
            <div><span>状态</span><strong>{{ selectedSticker.analyzed ? "已分析" : "待分析" }}</strong></div>
            <div><span>发送/收到</span><strong>{{ selectedSticker.send_count || 0 }}/{{ selectedSticker.recv_count || 0 }}</strong></div>
            <div><span>Embedding</span><strong>{{ selectedSticker.embedding_status || "-" }}</strong></div>
          </div>
          <pre>{{ JSON.stringify(selectedSticker, null, 2) }}</pre>
        </div>
        <div v-else class="empty">选择一张表情查看元数据。</div>
      </div>
      <div class="panel">
        <h2>候选详情</h2>
        <div v-if="selectedCandidate" class="sticker-detail">
          <img v-if="selectedCandidate.hash" class="detail-thumb" :src="`/api/stickers/image/${selectedCandidate.hash}`" />
          <div class="recall-explain">
            <strong>{{ candidateVerdict(selectedCandidate) }}</strong>
            <span>{{ (selectedCandidate.match_reasons || selectedCandidate.candidate_sources || []).join(", ") || "无命中说明" }}</span>
          </div>
          <pre>{{ JSON.stringify(selectedCandidate, null, 2) }}</pre>
        </div>
        <div v-else class="empty">搜索后选择一个候选查看召回细节。</div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api, postJson } from "../api/client";
import type { StickerItem } from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

const toast = useToastStore();
const stickers = ref<StickerItem[]>([]);
const overview = ref<Record<string, any>>({});
const candidates = ref<any[]>([]);
const decisions = ref<any[]>([]);
const selectedSticker = ref<StickerItem | null>(null);
const selectedCandidate = ref<any | null>(null);
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择 JSON");
const searchText = ref("");
const searchTopK = ref(8);
const filterText = ref("");
const statusFilter = ref<"all" | "analyzed" | "pending" | "used">("all");
const decisionFilter = ref<"all" | "sent" | "skipped">("all");
const loading = ref(false);
const columns = [
  { key: "preview", label: "预览" },
  { key: "filename", label: "文件" },
  { key: "analyzed", label: "分析" },
  { key: "description", label: "描述" },
  { key: "keywords", label: "关键词" },
  { key: "recv_count", label: "收到" },
  { key: "send_count", label: "发送" },
  { key: "actions", label: "操作" }
];
const candidateColumns = [
  { key: "preview", label: "预览" },
  { key: "summary", label: "摘要" },
  { key: "score", label: "分数" },
  { key: "match_reasons", label: "命中原因" },
  { key: "action", label: "操作" }
];
const decisionColumns = [
  { key: "status", label: "结果" },
  { key: "selected_hash", label: "选中" },
  { key: "detail", label: "详情" }
];
const analyzedCount = computed(() => stickers.value.filter((item) => item.analyzed).length);
const pendingCount = computed(() => Math.max(0, stickers.value.length - analyzedCount.value));
const sendableCount = computed(() => stickers.value.filter((item) => item.analyzed && (item.embedding_status === "embedded" || item.embedding_status === "ok" || !item.embedding_status)).length);
const decisionRows = computed(() => decisions.value.map((item, index) => ({ key: `${item.ts || ""}:${index}`, ...item })));
const filteredStickers = computed(() => {
  const text = filterText.value.trim().toLowerCase();
  return stickers.value.filter((item) => {
    if (statusFilter.value === "analyzed" && !item.analyzed) return false;
    if (statusFilter.value === "pending" && item.analyzed) return false;
    if (statusFilter.value === "used" && !(item.send_count || item.recv_count)) return false;
    if (!text) return true;
    return [
      item.hash,
      item.filename,
      item.description,
      item.summary,
      item.ocr_text,
      ...(item.keywords || []),
      ...(item.emotions || []),
      ...(item.scenes || []),
      ...(item.visual_subjects || []),
      ...(item.aliases || [])
    ].join(" ").toLowerCase().includes(text);
  });
});
const filteredDecisionRows = computed(() => {
  return decisionRows.value.filter((item) => {
    const sent = item.action === "send" || item.result === "sent";
    if (decisionFilter.value === "sent") return sent;
    if (decisionFilter.value === "skipped") return !sent;
    return true;
  });
});
const topCandidateScore = computed(() => {
  const scores = candidates.value.map((item) => Number(item.final_score ?? item.score)).filter(Number.isFinite);
  return scores.length ? Math.max(...scores) : 0;
});
const candidateSourceSummary = computed(() => {
  const sources = new Set<string>();
  for (const item of candidates.value) {
    for (const source of item.match_reasons || item.candidate_sources || []) sources.add(String(source));
  }
  return sources.size ? Array.from(sources).slice(0, 3).join(", ") : "-";
});
const canCopyDiagnostics = computed(() => stickers.value.length || candidates.value.length || decisions.value.length);
function formatScore(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(3) : "-";
}
function decisionDetail(row: any) {
  return JSON.stringify({
    reason: row.reason || row.decision?.reason || "",
    failure_reason: row.failure_reason || row.decision?.failure_reason || "",
    placement: row.placement || row.decision?.placement || "",
    candidates: (row.candidates || []).slice(0, 3).map((item: any) => item.hash || item.selected_hash || item.summary),
  }, null, 2);
}
function selectSticker(item: StickerItem) {
  selectedSticker.value = item;
}
function candidateVerdict(row: any) {
  const score = Number(row.final_score ?? row.score);
  if (Number.isFinite(score) && score >= 0.75) return "高置信候选";
  if ((row.match_reasons || row.candidate_sources || []).length) return "有召回线索";
  return "弱匹配候选";
}
async function copySticker(item: StickerItem) {
  await copyJson(item, "表情元数据已复制");
}
async function copyCandidate(item: any) {
  await copyJson(item, "召回候选已复制");
}
async function copyDiagnostics() {
  await copyJson({
    overview: overview.value,
    filter: { text: filterText.value, status: statusFilter.value, decision: decisionFilter.value },
    metrics: {
      total: stickers.value.length,
      analyzed: analyzedCount.value,
      pending: pendingCount.value,
      sendable: sendableCount.value,
      candidates: candidates.value.length,
      decisions: decisions.value.length
    },
    stickers: filteredStickers.value.slice(0, 100),
    candidates: candidates.value,
    decisions: filteredDecisionRows.value
  }, "表情包诊断已复制");
}
async function load(announce = true) {
  loading.value = true;
  error.value = "";
  if (announce) actionResult.value = "";
  try {
    const [overviewPayload, listPayload, decisionPayload] = await Promise.all([
      api<Record<string, any>>("/api/stickers"),
      api<{ stickers: StickerItem[] }>("/api/stickers/list?limit=500"),
      api<{ decisions: any[] }>("/api/sticker/decisions?limit=20"),
    ]);
    overview.value = overviewPayload || {};
    stickers.value = listPayload.stickers || [];
    decisions.value = decisionPayload.decisions || [];
    if (selectedSticker.value && !stickers.value.some((item) => item.hash === selectedSticker.value?.hash)) {
      selectedSticker.value = null;
    } else if (!selectedSticker.value) {
      selectedSticker.value = stickers.value[0] || null;
    }
    if (announce) actionResult.value = `已加载 ${stickers.value.length} 张表情包`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载表情包失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function upload(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  const form = new FormData();
  form.append("file", file);
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await api("/api/sticker/upload", { method: "POST", body: form });
    actionResult.value = "表情包上传完成";
    toast.push("上传完成");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "上传失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
    (event.target as HTMLInputElement).value = "";
  }
}
async function analyze(hash: string) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/sticker/analyze", { hash });
    actionResult.value = "已触发表情分析";
    toast.push("已触发分析");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "分析失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function analyzePending() {
  if (!window.confirm("确定批量分析最多 10 张待处理表情包？")) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/sticker/analyze", { count: 10 });
    actionResult.value = "已触发批量分析";
    toast.push("已触发批量分析");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "批量分析失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function remove(hash: string) {
  if (!window.confirm("确定删除这张表情包？")) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/sticker/delete", { hash });
    actionResult.value = "表情包已删除";
    toast.push("已删除");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "删除失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function reindex() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/sticker/reindex", { embeddings: true });
    actionResult.value = "表情索引已重建";
    toast.push("索引已重建");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "重建索引失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function search() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<{ candidates: any[] }>("/api/sticker/search", { text: searchText.value, top_k: searchTopK.value });
    candidates.value = payload.candidates || [];
    selectedCandidate.value = candidates.value[0] || null;
    actionResult.value = `召回完成，返回 ${candidates.value.length} 个候选`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "召回搜索失败";
    candidates.value = [];
    selectedCandidate.value = null;
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
onMounted(load);
</script>

<style scoped>
.thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
}

.toolbar input[type="text"],
.toolbar input:not([type]) {
  min-width: min(360px, 100%);
  flex: 1 1 260px;
}

.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.row-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.runtime-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
}

.runtime-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}

.runtime-grid span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.runtime-grid strong {
  display: block;
  margin-top: 5px;
  word-break: break-all;
}

.runtime-grid.compact {
  margin-bottom: 12px;
}

.sticker-detail {
  display: grid;
  gap: 12px;
}

.detail-thumb {
  width: 120px;
  height: 120px;
  object-fit: contain;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #0c0d10;
}

.recall-explain {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
}

.recall-explain strong,
.recall-explain span {
  display: block;
}

.recall-explain span {
  color: var(--muted);
  margin-top: 5px;
}
</style>
